import { useCallback, useState } from 'react'
import { type IntlShape } from 'react-intl'

import { apmCaptureError } from '../../apm'
import { useOpenableControls } from '../../generic-hooks'
import { useRefetchApiOrdersSwrGetOrdersInfinite } from '../../order'
import { directContactFormAccessorSubjectRequiresLogin } from '../model/directContactFormAccessorSubjectRequiresLogin'
import { DIRECT_CONTACT_FORM_ORDERS_PER_REQUEST } from '../model/directContactFormConstants'

import { useDirectContactFormOpenLoginModalForLoggedOutOrderSubject } from './useDirectContactFormOpenLoginModalForLoggedOutOrderSubject'

type AfterLoggedInSideEffects = Promise<string | undefined>

type UseDirectContactFormSubjectLoginModalParameters = {
  hasPrefilledOrder?: boolean
  intl: IntlShape
  isContactFormOpen?: boolean
  isCustomerLoggedIn: boolean
  selectedSubject: string
}

type UseDirectContactFormSubjectLoginModalResult = {
  handleLoginModalClose: () => void
  handleLoginSuccess: (
    afterLoggedInSideEffectsWithPreferredLanguage: AfterLoggedInSideEffects,
  ) => void
  handleOrderRequestAuthenticationError: () => void
  isLoggedIn: boolean
  loginModalControls: ReturnType<typeof useOpenableControls>
}

const captureDirectContactFormOrderLoginError = (error: unknown): void => {
  apmCaptureError(Error.isError(error) ? error : new Error(String(error)))
}

const refreshOrdersAfterLogin = ({
  afterLoggedInSideEffects,
  onComplete,
  refetchInfiniteOrders,
}: {
  afterLoggedInSideEffects: AfterLoggedInSideEffects
  onComplete: () => void
  refetchInfiniteOrders: () => Promise<unknown>
}): void => {
  void (async (): Promise<void> => {
    try {
      await afterLoggedInSideEffects
    } catch (error) {
      captureDirectContactFormOrderLoginError(error)
    }

    try {
      await refetchInfiniteOrders()
    } catch (error) {
      captureDirectContactFormOrderLoginError(error)
    } finally {
      onComplete()
    }
  })()
}

export const useDirectContactFormSubjectLoginModal = ({
  hasPrefilledOrder,
  intl,
  isContactFormOpen = true,
  isCustomerLoggedIn,
  selectedSubject,
}: UseDirectContactFormSubjectLoginModalParameters): UseDirectContactFormSubjectLoginModalResult => {
  const loginModalControls = useOpenableControls()
  const { handleClose, handleOpen } = loginModalControls
  const refetchInfiniteOrders = useRefetchApiOrdersSwrGetOrdersInfinite(
    DIRECT_CONTACT_FORM_ORDERS_PER_REQUEST,
  )
  const [isLoggedInAfterCheck, setIsLoggedInAfterCheck] = useState(false)
  const [orderAuthenticationFailed, setOrderAuthenticationFailed] =
    useState(false)
  const isLoggedIn =
    !orderAuthenticationFailed && (isCustomerLoggedIn || isLoggedInAfterCheck)

  const completeLogin = useCallback((): void => {
    setIsLoggedInAfterCheck(true)
    setOrderAuthenticationFailed(false)
    handleClose()
  }, [handleClose])
  const handleLoginSuccess = useCallback(
    (afterLoggedInSideEffects: AfterLoggedInSideEffects): void => {
      refreshOrdersAfterLogin({
        afterLoggedInSideEffects,
        onComplete: completeLogin,
        refetchInfiniteOrders,
      })
    },
    [completeLogin, refetchInfiniteOrders],
  )
  const handleOrderRequestAuthenticationError = useCallback((): void => {
    setOrderAuthenticationFailed(true)
    handleOpen()
  }, [handleOpen])
  const shouldOpenLoginModal = directContactFormAccessorSubjectRequiresLogin({
    hasPrefilledOrder,
    intl,
    subject: selectedSubject,
  })

  useDirectContactFormOpenLoginModalForLoggedOutOrderSubject({
    isContactFormOpen,
    isLoggedIn,
    onOpen: handleOrderRequestAuthenticationError,
    selectedSubject,
    shouldOpenLoginModal,
  })

  return {
    handleLoginModalClose: handleClose,
    handleLoginSuccess,
    handleOrderRequestAuthenticationError,
    isLoggedIn,
    loginModalControls,
  }
}
